

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ThemeProvider } from './components/theme-provider';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingProvider } from './components/LoadingSpinner';
import { Toaster } from './components/ui/sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import UserSelector from './components/UserSelector';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AccountDeletion from './components/AccountDeletion';
import WelcomeScreen from './components/WelcomeScreen';
import TelegramOnboarding from './components/TelegramOnboarding';
import TrainerDashboard from './pages/TrainerDashboard';
import ClientDashboard from './pages/ClientDashboard';
import { buildApiUrl } from './config/api';
import { getApiAccessToken, setApiAccessToken, clearApiAccessToken } from './config/authToken';
import { toast } from 'sonner';
import { Button } from './components/ui/button';
import { useI18n } from './i18n/context';
import { translateKey } from './i18n/messages';
import { getStoredUiLang } from './lib/uiLocale';
import { initNativeStatusBar } from './lib/nativeStatusBar';

const GOOGLE_WEB_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

function readResetPasswordTokenFromUrl() {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path !== '/reset-password') return null;
  const t = new URLSearchParams(window.location.search).get('token');
  return t && String(t).trim() ? String(t).trim() : null;
}

function normalizedPathname() {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname.replace(/\/$/, '') || '/';
}

/** Страницы юридических документов без полного SPA-boostrap (политика, условия, удаление аккаунта). */
function readLegalPageFromUrl() {
  const path = normalizedPathname();
  if (path === '/privacy') return 'privacy';
  if (path === '/terms') return 'terms';
  if (path === '/account-deletion') return 'accountDeletion';
  return null;
}

function TelegramBootErrorScreen({ detail, hint, onRetry, onWebLogin, t }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
      <p className="max-w-md text-foreground">
        {detail || t('app.telegramLoginFailed')}
      </p>
      {hint ? (
        <p className="max-w-md text-sm text-muted-foreground">{hint}</p>
      ) : null}
      <p className="max-w-md text-sm text-muted-foreground">
        {t('app.telegramLoginHint')}
      </p>
      <div className="flex w-full max-w-sm flex-col gap-2 sm:flex-row sm:justify-center">
        <Button type="button" className="w-full sm:w-auto" onClick={onRetry}>
          {t('app.refresh')}
        </Button>
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onWebLogin}>
          {t('app.loginWithEmail')}
        </Button>
      </div>
    </div>
  );
}

function App() {
  const { t } = useI18n();

  useEffect(() => {
    const isNative = !!(
      typeof window !== 'undefined' &&
      window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    );
    if (isNative) {
      document.documentElement.classList.add('capacitor-native');
      initNativeStatusBar();
    }
  }, []);

  const [currentUser, setCurrentUser] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [isLoadingUserContext, setIsLoadingUserContext] = useState(false);
  const [userContextError, setUserContextError] = useState(null);
  const [contextRetryToken, setContextRetryToken] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasEnteredAuth, setHasEnteredAuth] = useState(false);
  const [telegramAuthFailure, setTelegramAuthFailure] = useState(null);
  const [resetPasswordToken, setResetPasswordToken] = useState(readResetPasswordTokenFromUrl);
  const [legalPage, setLegalPage] = useState(readLegalPageFromUrl);
  const trainerPrefetchRef = useRef(null);

  // Сессия: Telegram Mini App (без экрана email/пароль) → иначе localStorage
  useEffect(() => {
    const bootstrap = async () => {
      try {
      if (typeof window !== 'undefined') {
        const path = window.location.pathname.replace(/\/$/, '') || '/';
        const isLegalPath =
          path === '/privacy' || path === '/terms' || path === '/account-deletion';
        let hasSavedUser = false;
        try {
          hasSavedUser = !!localStorage.getItem('currentUser');
        } catch {
          // ignore
        }
        /* Без сохранённой сессии юридические URL открываем сразу (без Telegram bootstrap). */
        if (isLegalPath && !hasSavedUser) {
          setIsInitialized(true);
          return;
        }
        if (path === '/reset-password' && new URLSearchParams(window.location.search).get('token')) {
          setIsInitialized(true);
          return;
        }
      }

      const sp = new URLSearchParams(window.location.search);
      if (sp.get('google_auth') === 'error') {
        const errReason = sp.get('reason') || 'unknown';
        const p = new URLSearchParams(window.location.search);
        p.delete('google_auth');
        p.delete('reason');
        const q = p.toString();
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash || ''}`
        );
        const loc = getStoredUiLang();
        const map = {
          invalid_state: translateKey(loc, 'app.google.invalid_state'),
          email_not_verified: translateKey(loc, 'app.google.email_not_verified'),
          account_conflict: translateKey(loc, 'app.google.account_conflict'),
          not_configured: translateKey(loc, 'app.google.not_configured'),
          server_error: translateKey(loc, 'app.google.server_error'),
          user_create_failed: translateKey(loc, 'app.google.user_create_failed'),
        };
        toast.error(
          map[errReason] || translateKey(loc, 'app.google.unknown', { reason: errReason })
        );
      }

      // Пользователь выбрал «Войти по email» — не дергаем Telegram API, пока флаг в сессии
      const skipTelegramAuth =
        typeof sessionStorage !== 'undefined' &&
        sessionStorage.getItem('mad_skip_telegram_auth') === '1';

      // Скрипт Telegram подмешивает WebApp и в обычном браузере; реальный Mini App даёт непустой initData.
      const tw = window.Telegram?.WebApp;
      let initData = '';
      if (tw) {
        try {
          tw.ready();
          tw.expand?.();
        } catch {
          // ignore
        }
        initData = tw.initData || '';
        for (let i = 0; i < 40 && !initData; i += 1) {
          await new Promise((r) => setTimeout(r, 50));
          initData = tw.initData || '';
        }
      }

      if (!skipTelegramAuth && tw && initData) {
        try {
          const response = await fetch(buildApiUrl('/auth/telegram'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ initData }),
          });
          const result = await response.json().catch(() => ({}));
          if (response.ok) {
            try {
              sessionStorage.removeItem('mad_skip_telegram_auth');
            } catch {
              // ignore
            }
            if (result.access_token) {
              setApiAccessToken(result.access_token);
            }
            const { access_token: _at, ...user } = result;
            setCurrentUser(user);
            setHasEnteredAuth(true);
            localStorage.setItem('currentUser', JSON.stringify(user));
            return;
          }
          const loc = getStoredUiLang();
          setTelegramAuthFailure({
            detail:
              result.error ||
              (response.status
                ? translateKey(loc, 'app.telegram.http', { status: response.status })
                : translateKey(loc, 'app.telegram.genericFail')),
            hint: result.hint,
          });
        } catch (e) {
          console.error('Telegram auth:', e);
          const loc = getStoredUiLang();
          setTelegramAuthFailure({
            detail: e?.message || translateKey(loc, 'app.telegram.network'),
          });
        }
        return;
      }

      const savedUser = localStorage.getItem('currentUser');
      const savedRole = localStorage.getItem('currentRole');

      if (savedUser) {
        try {
          const user = JSON.parse(savedUser);
          setCurrentUser(user);
          setHasEnteredAuth(true);
          if (savedRole) {
            setCurrentRole(savedRole);
          }
        } catch (e) {
          console.error('Error parsing saved user:', e);
          localStorage.removeItem('currentUser');
          localStorage.removeItem('currentRole');
        }
      }
      } finally {
        setIsInitialized(true);
      }
    };

    bootstrap();
  }, []);

  // Allow child components to request user updates (e.g. avatar change)
  useEffect(() => {
    const handler = (e) => {
      const patch = e?.detail?.patch || null;
      if (!patch || typeof patch !== 'object') return;

      setCurrentUser((prev) => {
        if (!prev) return prev;
        const next = { ...prev, ...patch };
        try {
          localStorage.setItem('currentUser', JSON.stringify(next));
        } catch (err) {
          // ignore
        }
        return next;
      });

      setUserContext((prev) => {
        if (!prev || !prev.user) return prev;
        return { ...prev, user: { ...prev.user, ...patch } };
      });
    };

    window.addEventListener('mad:userUpdated', handler);
    return () => window.removeEventListener('mad:userUpdated', handler);
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setUserContext(null);
      setUserContextError(null);
      setIsLoadingUserContext(false);
      return;
    }

    if (currentUser.telegram_profile_complete === false) {
      setUserContext(null);
      setUserContextError(null);
      setIsLoadingUserContext(false);
      return;
    }

    let cancelled = false;
    const userId = currentUser.id;

    // Prefetch trainer home data in parallel with context — saves ~200ms
    if (currentUser.is_trainer) {
      const bearer = getApiAccessToken();
      const prefetchOpts = { credentials: 'include' };
      if (bearer) prefetchOpts.headers = { Authorization: `Bearer ${bearer}` };
      trainerPrefetchRef.current = {
        workouts: fetch(buildApiUrl(`/workouts?trainer_id=${userId}`), prefetchOpts)
          .then(r => r.ok ? r.json() : []).catch(() => null),
        locations: fetch(buildApiUrl(`/trainer-locations/${userId}`), prefetchOpts)
          .then(r => r.ok ? r.json() : []).catch(() => null),
      };
    }

    (async () => {
      // Если контекст уже есть для того же пользователя — фоновый refetch (например после mad:refetchUserContext).
      // Не показываем полноэкранный лоадер: иначе размонтируется TrainerDashboard и сбрасывается вкладка (дом вместо «Клиенты»).
      const sameUserContextReady =
        userContext != null && userContext.user?.id === userId;
      if (!sameUserContextReady) {
        setIsLoadingUserContext(true);
      }
      setUserContextError(null);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(buildApiUrl(`/users/${userId}/context`), {
          signal: controller.signal,
          credentials: 'include',
        });

        clearTimeout(timeoutId);
        if (cancelled) return;

        if (!response.ok) {
          const err = new Error(`HTTP error! status: ${response.status}`);
          err.status = response.status;
          throw err;
        }

        const context = await response.json();
        if (cancelled) return;

        setUserContext(context);

        if (context.roles?.is_trainer) {
          setCurrentRole('trainer');
        } else if (context.roles?.is_client) {
          setCurrentRole('client');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading user context:', error);

        const status = error?.status ?? error?.response?.status;
        if (status === 401 || status === 403) {
          try {
            clearApiAccessToken();
            localStorage.removeItem('currentUser');
            localStorage.removeItem('currentRole');
          } catch {}
          setCurrentUser(null);
          setCurrentRole(null);
          setUserContext(null);
          setUserContextError(null);
          return;
        }

        let message = t('app.contextError.default');
        if (error?.name === 'AbortError') {
          message = t('app.contextError.timeout');
        } else if (typeof status === 'number' && status >= 500) {
          message = t('app.contextError.server');
        } else if (typeof status === 'number' && status >= 400) {
          message = t('app.contextError.client');
        }

        setUserContext(null);
        setUserContextError(message);
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) {
          setIsLoadingUserContext(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser, contextRetryToken, t]);

  useEffect(() => {
    const onRefetch = () => setContextRetryToken((x) => x + 1);
    window.addEventListener('mad:refetchUserContext', onRefetch);
    return () => window.removeEventListener('mad:refetchUserContext', onRefetch);
  }, []);

  // Внутренняя навигация на /privacy, /terms, /account-deletion без полного reload (Capacitor).
  useEffect(() => {
    const onOpenPrivacy = () => {
      try {
        if (normalizedPathname() !== '/privacy') {
          window.history.pushState({}, '', '/privacy');
        }
      } catch {
        // ignore
      }
      setLegalPage('privacy');
    };
    const onOpenTerms = () => {
      try {
        if (normalizedPathname() !== '/terms') {
          window.history.pushState({}, '', '/terms');
        }
      } catch {
        // ignore
      }
      setLegalPage('terms');
    };
    const onOpenAccountDeletion = () => {
      try {
        if (normalizedPathname() !== '/account-deletion') {
          window.history.pushState({}, '', '/account-deletion');
        }
      } catch {
        // ignore
      }
      setLegalPage('accountDeletion');
    };
    const onPopState = () => setLegalPage(readLegalPageFromUrl());
    window.addEventListener('mad:openPrivacy', onOpenPrivacy);
    window.addEventListener('mad:openTerms', onOpenTerms);
    window.addEventListener('mad:openAccountDeletion', onOpenAccountDeletion);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('mad:openPrivacy', onOpenPrivacy);
      window.removeEventListener('mad:openTerms', onOpenTerms);
      window.removeEventListener('mad:openAccountDeletion', onOpenAccountDeletion);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  // Единый тост при постановке мутации в офлайн-очередь (axios / fetch /api)
  useEffect(() => {
    let timer;
    const onQueued = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.message(t('app.offlineSaved'), { duration: 4500 });
      }, 200);
    };
    window.addEventListener('mad:offlineMutationQueued', onQueued);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mad:offlineMutationQueued', onQueued);
    };
  }, [t]);

  const handleUserSelect = (user) => {
    if (user?.access_token) {
      setApiAccessToken(user.access_token);
    }
    const { access_token: _at, ...rest } = user || {};
    setCurrentUser(rest);
    localStorage.setItem('currentUser', JSON.stringify(rest));
  };

  const handleRoleChange = (newRole) => {
    setCurrentRole(newRole);
    // Сохраняем роль в localStorage
    localStorage.setItem('currentRole', newRole);
  };

  const handleTelegramOnboardingComplete = useCallback((user) => {
    const { access_token: _at, ...rest } = user || {};
    setCurrentUser(rest);
    try {
      localStorage.setItem('currentUser', JSON.stringify(rest));
    } catch {
      // ignore
    }
    window.dispatchEvent(new CustomEvent('mad:userUpdated', { detail: { patch: rest } }));
  }, []);

  const handleLogout = () => {
    clearApiAccessToken();
    // Best-effort server-side logout (clear HttpOnly cookie)
    try {
      fetch(buildApiUrl('/users/logout'), { method: 'POST', credentials: 'include' }).catch(() => {});
    } catch {
      // ignore
    }

    // Очищаем localStorage
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentRole');
    // Сбрасываем состояние
    setCurrentUser(null);
    setCurrentRole(null);
    setUserContext(null);
    setUserContextError(null);
    setHasEnteredAuth(false);
  };

  const needsTelegramOnboarding = !!currentUser && currentUser.telegram_profile_complete === false;

  const isTelegramAuth =
    currentUser?.telegram_auth === true || userContext?.user?.telegram_auth === true;

  const renderDashboard = () => {
    if (!currentUser) {
      return null;
    }

    if (userContextError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background p-6 text-center">
          <p className="max-w-md text-muted-foreground">{userContextError}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button type="button" onClick={() => setContextRetryToken((x) => x + 1)}>
              {t('common.retry')}
            </Button>
            {!isTelegramAuth && (
              <Button type="button" variant="outline" onClick={handleLogout}>
                {t('common.exit')}
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (isLoadingUserContext || !userContext) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span>{t('app.loadContext')}</span>
          </div>
        </div>
      );
    }

    if (!currentRole) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
          <p className="max-w-md text-muted-foreground">
            {t('app.noRole')}
          </p>
          {!isTelegramAuth && (
            <Button type="button" variant="outline" onClick={handleLogout}>
              {t('common.exit')}
            </Button>
          )}
        </div>
      );
    }

    if (currentRole === 'trainer') {
      return (
        <TrainerDashboard 
          currentUser={currentUser}
          userContext={userContext}
          onLogout={handleLogout}
          onRoleChange={handleRoleChange}
          prefetchRef={trainerPrefetchRef}
        />
      );
    } else if (currentRole === 'client') {
      return (
        <ClientDashboard 
          currentUser={currentUser}
          userContext={userContext}
          onLogout={handleLogout}
          onRoleChange={handleRoleChange}
        />
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
        <p className="text-muted-foreground">{t('app.loadInterface')}</p>
        <Button type="button" variant="outline" onClick={() => window.location.reload()}>
          {t('common.reload')}
        </Button>
      </div>
    );
  };

  // Показываем загрузку пока не инициализировались
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        <p className="text-sm text-gray-600">{t('app.initializing')}</p>
      </div>
    );
  }

  const closeLegal = () => {
    try {
      window.history.replaceState({}, '', '/');
    } catch {
      // ignore
    }
    setLegalPage(null);
  };

  // Публичные URL: /privacy (Play), /terms, /account-deletion (удаление аккаунта в Play Console).
  if (legalPage && !currentUser) {
    return (
      <ErrorBoundary>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          forcedTheme="light"
          disableTransitionOnChange
        >
          {legalPage === 'privacy' ? (
            <PrivacyPolicy onBack={closeLegal} />
          ) : legalPage === 'terms' ? (
            <TermsOfService onBack={closeLegal} />
          ) : (
            <AccountDeletion onBack={closeLegal} />
          )}
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        forcedTheme="light"
        disableTransitionOnChange
      >
        <LoadingProvider>
          <div className="App min-h-[100dvh] min-h-[100vh] w-full max-w-[100vw] min-w-0 overflow-x-hidden flex flex-col flex-1">
            {!currentUser ? (
              resetPasswordToken ? (
                <ResetPasswordScreen
                  token={resetPasswordToken}
                  onDone={() => {
                    setResetPasswordToken(null);
                    try {
                      window.history.replaceState({}, '', '/');
                    } catch {
                      // ignore
                    }
                    setHasEnteredAuth(true);
                  }}
                />
              ) : telegramAuthFailure ? (
                <TelegramBootErrorScreen
                  t={t}
                  detail={telegramAuthFailure.detail}
                  hint={telegramAuthFailure.hint}
                  onRetry={() => window.location.reload()}
                  onWebLogin={() => {
                    try {
                      sessionStorage.setItem('mad_skip_telegram_auth', '1');
                    } catch {
                      // ignore
                    }
                    setTelegramAuthFailure(null);
                    setHasEnteredAuth(true);
                  }}
                />
              ) : !hasEnteredAuth ? (
                <WelcomeScreen onContinue={() => setHasEnteredAuth(true)} />
              ) : GOOGLE_WEB_CLIENT_ID ? (
                <GoogleOAuthProvider clientId={GOOGLE_WEB_CLIENT_ID}>
                  <UserSelector onUserSelect={handleUserSelect} />
                </GoogleOAuthProvider>
              ) : (
                <UserSelector onUserSelect={handleUserSelect} />
              )
            ) : needsTelegramOnboarding ? (
              <TelegramOnboarding user={currentUser} onCompleted={handleTelegramOnboardingComplete} />
            ) : (
              renderDashboard()
            )}
          </div>
          {/* Поверх дашборда — юридические страницы не размонтируют экран приложения */}
          {legalPage === 'privacy' && (
            <div className="fixed inset-0 z-[80] overflow-y-auto bg-background">
              <PrivacyPolicy onBack={closeLegal} />
            </div>
          )}
          {legalPage === 'accountDeletion' && (
            <div className="fixed inset-0 z-[80] overflow-y-auto bg-background">
              <AccountDeletion onBack={closeLegal} />
            </div>
          )}
          <Toaster position="top-center" richColors />
        </LoadingProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
